import React, { useState, useEffect, useMemo } from "react";
import { Feedback, Confetti, Speaker, playCorrect, playWrong } from "@/components/Bits";
import { OptionTile, PromptView, DoneCard, RoundHeader } from "@/components/Cells";
import { recordAnswer, completeActivity } from "@/lib/progress";
import { speak } from "@/lib/speech";

/* Generic quiz runner. items: [{ ask, voice?, correctKey, prompt, options, cols?, hideLabels? }] */
export default function QuizRunner({ title, category, modeId, build, items: fixedItems, backTo = "/learn", correctVoice }) {
  const [seed, setSeed] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = useMemo(() => fixedItems || build(seed), [seed, fixedItems, build]);
  const [idx, setIdx] = useState(0);
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState(null);
  const [done, setDone] = useState(false);
  const item = items[idx];

  useEffect(() => { if (item?.voice) speak(item.voice); }, [item]);

  const choose = (key) => {
    if (state === "correct") return;
    setSelected(key);
    const ok = key === item.correctKey;
    recordAnswer(category, ok);
    if (ok) { const o = item.options.find(x => x.key === key); playCorrect(correctVoice ? correctVoice(item) : (o?.label || null)); setState("correct"); }
    else { playWrong(); speak("Almost, try again"); setState("wrong"); }
  };
  const next = () => {
    setState(null); setSelected(null);
    if (idx + 1 >= items.length) { completeActivity(category, modeId); setDone(true); speak("Adventure complete! Great job!"); }
    else setIdx(idx + 1);
  };
  const again = () => { setSeed(s => s + 1); setIdx(0); setDone(false); };

  if (done) return <DoneCard onAgain={again} backTo={backTo} backLabel="Back" subtitle={`You finished ${title}! Stars earned.`} />;

  const cols = item.cols || (item.options.length > 3 ? 2 : item.options.length);
  const colCls = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-3 sm:grid-cols-4" }[cols] || "grid-cols-3";

  return (
    <div className="ll-bg min-h-screen pb-32">
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <RoundHeader idx={idx} total={items.length} />
        <div className="ll-card p-5 sm:p-8">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-purple-900">{title}</h2>
            {item.voice && <Speaker text={item.voice} testId="question-speaker" />}
          </div>
          <div className="mt-4"><PromptView p={item.prompt} /></div>
          <div className="mt-4 text-center text-purple-800 font-display text-xl sm:text-2xl font-bold" data-testid="question-text">{item.ask}</div>
          <div className={`grid ${colCls} gap-3 sm:gap-4 mt-5`}>
            {item.options.map(opt => (
              <OptionTile key={opt.key} opt={opt} state={state} selected={selected === opt.key} onClick={() => choose(opt.key)} hideLabel={item.hideLabels} testId={`option-${opt.key}`} />
            ))}
          </div>
        </div>
      </div>
      <Feedback state={state} onNext={next} onRetry={() => { setState(null); setSelected(null); }} />
      <Confetti show={state === "correct"} />
    </div>
  );
}
